/**
 * Admin write schemas for WebsiteMediaAsset (Phase 5 - CMS write API).
 *
 * Upload flow (single-PUT for small files, multipart for large ones):
 *   POST /admin/website/media/upload-init     - mints an asset row + presigned
 *        URL(s). Small files → one PUT URL. Large files → a multipart session
 *        (uploadId + one presigned URL per part).
 *   <client PUTs bytes directly to S3 - one PUT, or N part PUTs>
 *   POST /admin/website/media/upload-finalize - completes multipart (if any),
 *        HEADs S3, verifies size matches.
 *   POST /admin/website/media/upload-abort    - discards a failed multipart.
 *
 * Per-kind size caps (below) bound what an editor can push. Files above
 * MULTIPART_THRESHOLD_BYTES are uploaded in parts so large video is reliable.
 */

import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

export const WebsiteMediaKindSchema = z.enum(['IMAGE', 'VIDEO', 'DOCUMENT']);
export type WebsiteMediaKind = z.infer<typeof WebsiteMediaKindSchema>;

const MB = 1024 * 1024;
/** Files larger than this use multipart upload. */
export const MULTIPART_THRESHOLD_BYTES = 16 * MB;
/** Part size for multipart uploads (S3 minimum is 5MB except the last part). */
export const MULTIPART_PART_SIZE_BYTES = 16 * MB;
/** Hard size cap per media kind. */
export const MEDIA_MAX_BYTES: Record<WebsiteMediaKind, number> = {
  IMAGE: 15 * MB,
  VIDEO: 2 * 1024 * MB, // 2 GB
  DOCUMENT: 50 * MB,
};

export const InitMediaUploadRequestSchema = z
  .object({
    kind: WebsiteMediaKindSchema,
    mimeType: z.string().min(3).max(120),
    sizeBytes: z.number().int().positive().max(MEDIA_MAX_BYTES.VIDEO), // absolute ceiling; per-kind refined below
    fileName: z.string().min(1).max(255),
    folder: z.string().max(120).nullish(),
  })
  .superRefine((val, ctx) => {
    const cap = MEDIA_MAX_BYTES[val.kind];
    if (val.sizeBytes > cap) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sizeBytes'],
        message: `${val.kind} files are limited to ${Math.round(cap / MB)}MB`,
      });
    }
  });
export type InitMediaUploadRequest = z.infer<typeof InitMediaUploadRequestSchema>;

/** One presigned part PUT in a multipart session. */
export const MultipartPartUrlSchema = z.object({
  partNumber: z.number().int().positive(),
  url: z.string().url(),
});
export type MultipartPartUrl = z.infer<typeof MultipartPartUrlSchema>;

const InitSingleSchema = z.object({
  mode: z.literal('single'),
  assetId: UuidSchema,
  s3Key: z.string(),
  presignedUrl: z.string().url(),
  expiresAt: IsoDateTimeSchema,
});
const InitMultipartSchema = z.object({
  mode: z.literal('multipart'),
  assetId: UuidSchema,
  s3Key: z.string(),
  uploadId: z.string(),
  partSizeBytes: z.number().int().positive(),
  parts: z.array(MultipartPartUrlSchema).min(1),
  expiresAt: IsoDateTimeSchema,
});

export const InitMediaUploadResponseSchema = z.discriminatedUnion('mode', [
  InitSingleSchema,
  InitMultipartSchema,
]);
export type InitMediaUploadResponse = z.infer<typeof InitMediaUploadResponseSchema>;

/** Completed part: its number + the ETag S3 returned on the part PUT. */
export const CompletedPartSchema = z.object({
  partNumber: z.number().int().positive(),
  etag: z.string().min(1),
});
export type CompletedPart = z.infer<typeof CompletedPartSchema>;

export const FinalizeMediaUploadRequestSchema = z.object({
  assetId: UuidSchema,
  /** Multipart only: the session id + parts to assemble. Omit for single PUT. */
  uploadId: z.string().optional(),
  parts: z.array(CompletedPartSchema).optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationSeconds: z.number().int().positive().optional(),
});
export type FinalizeMediaUploadRequest = z.infer<typeof FinalizeMediaUploadRequestSchema>;

export const AbortMediaUploadRequestSchema = z.object({
  assetId: UuidSchema,
  uploadId: z.string(),
});
export type AbortMediaUploadRequest = z.infer<typeof AbortMediaUploadRequestSchema>;

export const UpdateMediaAssetRequestSchema = z.object({
  altText: z.string().max(500).nullish(),
  caption: z.string().max(1000).nullish(),
  folder: z.string().max(120).nullish(),
});
export type UpdateMediaAssetRequest = z.infer<typeof UpdateMediaAssetRequestSchema>;
